import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import _ from 'lodash';
//mui
import { Box, Popover, Stack, Typography, useTheme } from '@mui/material';
import { CurrencyBar, CurrencyWrap } from 'theme/layouts/currencyBar';
import { getCompanyBalances } from 'state/reducers/balance.reducers';
import { getCurrencies } from 'state/reducers/global.reducers';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { StatusType, TransferType, amountType, initialBalance } from 'constants/balance.constants';
import { getDir, selectTranslations } from 'state/reducers/translation.reducers';
import { getOpenPendingRequests } from 'state/reducers/requests.reducers';
import { getActivities } from 'state/reducers/activity.reducers';
import { ActivityTypes } from 'constants/activity.constants';
import PendingBalanceTable from 'sections/dashboard/PendingBalanceTable';
import { getActivitiesAsync } from 'services/activity.services';
import { getUserDetails } from 'state/reducers/user.reducers';
import { subDays } from 'date-fns';

export const formatNumber = (number?: number, type?: string) => {
  const shortNumber = number?.toFixed(2);
  const splitNumber = String(shortNumber).split('.');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline' }}>
      {type === amountType.FOR_USE && (
        <>
          {!number ? (
            <Typography variant={'h6'} fontFamily="RubikLight">
              0
            </Typography>
          ) : (
            <Box dir="ltr" sx={{ display: 'flex', alignItems: 'baseline' }}>
              <Typography variant="h6" fontFamily="RubikLight">
                {parseInt(splitNumber[0]).toLocaleString()}
              </Typography>
              {splitNumber[1] !== '00' && (
                <Typography variant="h6" fontFamily="RubikLight" sx={{ fontSize: '13px!important' }}>
                  .{splitNumber[1]}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
      {type === amountType.PENDING && (
        <>
          {!number ? (
            <Typography variant="h6" fontFamily="RubikLight">
              0
            </Typography>
          ) : (
            <Box dir="ltr" sx={{ display: 'flex', alignItems: 'baseline' }}>
              <Typography variant="h6" fontFamily="RubikLight">
                {parseInt(splitNumber[0]).toLocaleString()}
              </Typography>
              {splitNumber[1] !== '00' && (
                <Typography variant="h6" fontFamily="RubikLight" sx={{ fontSize: '12px' }}>
                  .{splitNumber[1]}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const renderHTML = (rawHTML: string) => React.createElement('span', { dangerouslySetInnerHTML: { __html: rawHTML } });
export default function Currency() {
  const t = useAppSelector(selectTranslations);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const dir = useAppSelector(getDir);
  const companyBalances = useAppSelector(getCompanyBalances);

  const companyBalancesList =
    companyBalances.length > 0 ? _.sortBy(companyBalances, 'amount').reverse() : initialBalance;
  const currencies = useAppSelector(getCurrencies);
  const user = useAppSelector(getUserDetails);
  const [page, setPage] = useState(0);
  const cardHeight = '94px';
  const itemsPerPage = 3;
  const addLeft = page * (-100 / itemsPerPage) + '%';
  const { balancesRequests } = useAppSelector(getOpenPendingRequests);
  const withdrawalsRequests = useAppSelector((state => state.pendingRequests.withdrawalRequests))
  const companyActivities = useAppSelector(getActivities);
  const [pendingDataToShow, setPendingDataToShow] = useState<any>(null);
  const [pendingCurrency, setPendingCurrency] = useState('');
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
    setPendingDataToShow(null);
    setPendingCurrency('');
  };

  const handlePendingAmountHover = (data: any, el: any) => {
    if (data.pendingAmount > 0 || data.pendingAmount < 0) {
      let balancesResults: any = [];
      let companyActivitiesResults: any = [];
      let companyWithdrawalsRequests: any = []
      if (balancesRequests) {
        balancesResults = balancesRequests?.filter(
          (balanceRequest: any) =>
            balanceRequest.currency?.id === data.currencyId &&
            (balanceRequest.status === StatusType.Pending ||
              balanceRequest.status === StatusType.PendingBoSignerReview) &&
            balanceRequest.type === TransferType.Topping
        );
      }
      if (companyActivities) {
        companyActivitiesResults = companyActivities.reduce(
          (total: any, activity: any) =>
            (activity.currencyId === data.currencyId &&
              activity.type === ActivityTypes.withdrawal &&
              (activity.status === StatusType.Pending || activity.status === StatusType.PendingBoSignerReview))
              ||
              (activity.pendingQuote?.buyCurrency?.id === data.currencyId &&
                activity.type === ActivityTypes.conversionActivities &&
                (activity.status === StatusType.Pending || activity.status === StatusType.PendingScheduler))
              ? [
                ...total,
                {
                  referenceNumber: activity.reference,
                  transactionType: activity.transactionType || ActivityTypes.withdrawal,
                  amount: activity.pendingQuote?.altshulerBuyAmount || activity.amount,
                  date: activity.createdAt,
                },
              ]
              : total
          ,
          []
        );
      }
      if (withdrawalsRequests) {
        companyWithdrawalsRequests = withdrawalsRequests.reduce(
          (total: any, activity: any) =>
            (activity.currency === data?.currency?.code &&
              (activity.status === StatusType.Pending))
              ? [
                ...total,
                {
                  referenceNumber: activity.reference,
                  transactionType: activity.transactionType || ActivityTypes.withdrawal,
                  amount: activity.pendingQuote?.altshulerBuyAmount || activity.amount,
                  date: activity.createdAt,
                },
              ]
              : total
          ,
          []
        );
      }
      setPendingDataToShow([
        ...balancesResults,
        ...companyActivitiesResults,
        ...companyWithdrawalsRequests
      ]);
      setPendingCurrency(data.currency.symbol);
      setAnchorEl(el);
    }
  };

  useEffect(() => {
    dispatch(getActivitiesAsync(user?.companyId, subDays(new Date(), 10), new Date()));
  }, []);

  return (
    <>
      <CurrencyWrap sx={{ height: cardHeight }}>
        {companyBalancesList.length > 0 && page > 0 && (
          <ArrowBackIosIcon
            className="arrowGallery"
            onClick={() => setPage(page - 1)}
            sx={{
              left: '4px',
              height: `calc( ${cardHeight} - 4px)`,
              transform: dir === 'rtl' ? 'scaleX(-1)' : '',
            }}
          />
        )}

        {currencies && companyBalancesList.length > 0 && (
          <CurrencyBar gap={{ xs: 0, sm: 10 }} left={addLeft}>
            {companyBalancesList.map((currency, index) => (
              <Box key={index} className="balanceItemWrap">
                <Stack className="balanceItem" spacing={0} sx={{ height: '94px' }}>
                  <Box sx={{ marginTop: '5px' }}>
                    <Stack direction="row" alignItems="baseline" sx={{ minWidth: '100px' }}>
                      <Typography variant="caption" fontFamily="RubikLight">
                        {renderHTML(
                          _.get(_.keyBy(currencies, 'id'), currency?.currencyId)?.symbol
                        )}
                      </Typography>
                      <Typography variant="semiCaption" color={theme.palette.root.basicDark_60}>
                        {_.get(_.keyBy(currencies, 'id'), currency?.currencyId)?.code}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="baseline"
                      fontFamily="RubikLight"
                      sx={{ width: '100%' }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          width: '100%',
                        }}
                      >
                        <Typography
                          variant="captionRegular"
                          sx={{ marginRight: '10px', textWrap: 'nowrap' }}
                        >
                          {t('page.dashboard.balances.available.text')}
                        </Typography>
                        {typeof currency?.amount !== 'string'
                          ? formatNumber(currency?.amount, amountType.FOR_USE)
                          : currency?.amount}
                      </Box>
                    </Stack>
                    <Box
                      sx={{
                        border: `0.5px solid ${theme.palette.root.basicDeeoGreen}`,
                        opacity: '0.2',
                      }}
                    ></Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        width: '100%',
                      }}
                      onMouseOver={_.debounce(
                        (e) => {
                          handlePendingAmountHover(currency, e.target);
                        },
                        300,
                        { maxWait: 300 }
                      )}
                      onMouseLeave={_.debounce(handleClose, 300)}
                    >
                      <Typography variant="captionRegular" sx={{ marginRight: '10px' }}>
                        {t('page.dashboard.balances.pending.text')}
                      </Typography>
                      {typeof currency?.amount !== 'string'
                        ? formatNumber(currency?.pendingAmount, amountType.PENDING)
                        : currency?.amount}
                    </Box>
                  </Box>
                </Stack>
              </Box>
            ))}
          </CurrencyBar>
        )}
        {companyBalancesList.length > 0 && itemsPerPage + page < companyBalancesList.length && (
          <ArrowForwardIosIcon
            onClick={() => setPage(page + 1)}
            className="arrowGallery"
            sx={{
              transform: dir === 'rtl' ? 'scaleX(-1)' : '',
              right: '2px',
              height: `calc( ${cardHeight} - 4px)`,
            }}
          />
        )}
      </CurrencyWrap>
      <Popover
        style={{ pointerEvents: 'none' }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <PendingBalanceTable currencyId={pendingCurrency} pendingDataToShow={pendingDataToShow} />
      </Popover>
    </>
  );
}
